import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Beaker } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import ImageUpload from '../../components/admin/ImageUpload';
import { getImageUrl } from '../../lib/utils';

const LabsManagement = () => {
  const [labs, setLabs] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: '',
    college_id: '',
    image: '',
    capacity: '',
    supervisor_name_ar: '',
    supervisor_name_en: '',
    location: '',
    is_published: true,
    order: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [labsRes, collegesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/labs/all`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        }),
        fetch(`${BACKEND_URL}/api/colleges/all`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        })
      ]);
      
      const labsData = await labsRes.json();
      const collegesData = await collegesRes.json();
      
      setLabs(labsData);
      setColleges(collegesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editing
      ? `${BACKEND_URL}/api/labs/${editing}`
      : `${BACKEND_URL}/api/labs/`;
    const method = editing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        fetchData();
        resetForm();
        alert('تم الحفظ بنجاح!');
      } else {
        alert('حدث خطأ أثناء الحفظ');
      }
    } catch (error) {
      console.error('Error saving lab:', error);
      alert('حدث خطأ أثناء الحفظ');
    }
  };

  const handleEdit = (lab) => {
    setEditing(lab.id);
    setFormData({
      name_ar: lab.name_ar || '',
      name_en: lab.name_en || '',
      description_ar: lab.description_ar || '',
      description_en: lab.description_en || '',
      college_id: lab.college_id || '',
      image: lab.image || '',
      capacity: lab.capacity || '',
      supervisor_name_ar: lab.supervisor_name_ar || '',
      supervisor_name_en: lab.supervisor_name_en || '',
      location: lab.location || '',
      is_published: lab.is_published !== undefined ? lab.is_published : true,
      order: lab.order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (lab) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المعمل؟')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/labs/${lab.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      
      if (response.ok) {
        fetchData();
        alert('تم الحذف بنجاح!');
      } else {
        alert('حدث خطأ أثناء الحذف');
      }
    } catch (error) {
      console.error('Error deleting lab:', error);
      alert('حدث خطأ أثناء الحذف');
    }
  };

  const resetForm = () => {
    setFormData({
      name_ar: '',
      name_en: '',
      description_ar: '',
      description_en: '',
      college_id: '',
      image: '',
      capacity: '',
      supervisor_name_ar: '',
      supervisor_name_en: '',
      location: '',
      is_published: true,
      order: 0
    });
    setEditing(null);
    setShowForm(false);
  };

  const getCollegeName = (collegeId) => {
    const college = colleges.find(c => c.id === collegeId);
    return college ? college.name_ar : 'غير محدد';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#0F388E] flex items-center gap-3">
          <Beaker className="w-10 h-10" />
          إدارة المعامل
        </h1>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-[#0F388E]">
            <Plus className="ml-2 w-5 h-5" />
            إضافة معمل
          </Button>
        )}
      </div>

      {!showForm && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {labs.map(lab => (
            <Card key={lab.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                {lab.image && (
                  <img 
                    src={getImageUrl(lab.image)} 
                    alt={lab.name_ar} 
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                )}
                <h3 className="text-xl font-bold text-[#0F388E] mb-2">{lab.name_ar}</h3>
                {lab.name_en && (
                  <p className="text-sm text-gray-500 mb-2">{lab.name_en}</p>
                )}
                <p className="text-sm text-gray-600 mb-3 line-clamp-3">{lab.description_ar}</p>
                
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-[#C9A040]">🏛️</span>
                    <span className="text-gray-700">{getCollegeName(lab.college_id)}</span>
                  </div>
                  {lab.capacity && (
                    <div className="flex items-center gap-2">
                      <span className="text-[#C9A040]">👥</span>
                      <span className="text-gray-700">السعة: {lab.capacity} طالب</span>
                    </div>
                  )}
                  {lab.supervisor_name_ar && (
                    <div className="flex items-center gap-2">
                      <span className="text-[#C9A040]">👨‍🔬</span>
                      <span className="text-gray-700">المشرف: {lab.supervisor_name_ar}</span>
                    </div>
                  )}
                  {lab.location && (
                    <div className="flex items-center gap-2">
                      <span className="text-[#C9A040]">📍</span>
                      <span className="text-gray-700">{lab.location}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 justify-end border-t pt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(lab)}
                    className="text-[#0F388E]"
                  >
                    <Edit2 className="w-4 h-4 ml-1" />
                    تعديل
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(lab)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 ml-1" />
                    حذف
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {labs.length === 0 && (
            <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
              <Beaker className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">لا توجد معامل مضافة بعد</p>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Beaker className="w-6 h-6" />
              {editing ? 'تعديل معمل' : 'إضافة معمل جديد'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg space-y-4 border border-blue-100">
                <h3 className="font-bold text-[#0F388E] text-lg">المعلومات الأساسية</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      اسم المعمل بالعربي *
                    </label>
                    <input
                      type="text"
                      value={formData.name_ar}
                      onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lab Name in English
                    </label>
                    <input
                      type="text"
                      value={formData.name_en}
                      onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الكلية التابع لها *
                  </label>
                  <select
                    value={formData.college_id}
                    onChange={(e) => setFormData({...formData, college_id: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    required
                  >
                    <option value="">اختر الكلية</option>
                    {colleges.map(college => (
                      <option key={college.id} value={college.id}>
                        {college.name_ar}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg space-y-4 border border-green-100">
                <h3 className="font-bold text-[#0F388E] text-lg">الوصف</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      وصف المعمل بالعربي *
                    </label>
                    <textarea
                      value={formData.description_ar}
                      onChange={(e) => setFormData({...formData, description_ar: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lab Description in English
                    </label>
                    <textarea
                      value={formData.description_en}
                      onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg space-y-4 border border-purple-100">
                <h3 className="font-bold text-[#0F388E] text-lg">معلومات إضافية</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      السعة (عدد الطلاب)
                    </label>
                    <input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الموقع
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      اسم المشرف بالعربي
                    </label>
                    <input
                      type="text"
                      value={formData.supervisor_name_ar}
                      onChange={(e) => setFormData({...formData, supervisor_name_ar: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Supervisor Name in English
                    </label>
                    <input
                      type="text"
                      value={formData.supervisor_name_en}
                      onChange={(e) => setFormData({...formData, supervisor_name_en: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    />
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg space-y-4 border border-indigo-100">
                <h3 className="font-bold text-[#0F388E] text-lg">صورة المعمل</h3>
                <ImageUpload
                  label="صورة المعمل"
                  value={formData.image}
                  onChange={(v) => setFormData({...formData, image: v})}
                />
              </div>

              {/* Settings */}
              <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                <h3 className="font-bold text-[#0F388E] text-lg">الإعدادات</h3>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_published"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                    className="w-5 h-5 text-[#0F388E] border-gray-300 rounded focus:ring-[#0F388E]"
                  />
                  <label htmlFor="is_published" className="text-gray-700">
                    نشر المعمل على الموقع
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  إلغاء
                </Button>
                <Button type="submit" className="bg-[#0F388E]">
                  {editing ? 'تحديث المعمل' : 'حفظ المعمل'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LabsManagement;
